/**
 * CE QUE L'ÉCRAN D'ENVOI DÉCIDE — lot A-bis, 29/10/2026.
 *
 * Les règles sont ici et non dans le composant, pour la même raison
 * qu'`enBase64` ne lit pas le fichier : ce qui se vérifie sans navigateur doit
 * pouvoir se vérifier sans navigateur. Le composant assemble, il ne décide
 * pas.
 */

import type { DestinatairesProposes } from './confirmation.js';

export interface Choix {
  readonly adresse: string;
  /** Ce que l'écran affiche à côté de la case : « Client », « Marie (compta) »… */
  readonly libelle: string;
  /** Coché par défaut à l'ouverture. */
  readonly parDefaut: boolean;
}

/**
 * Les adresses proposées, dans l'ordre d'affichage.
 *
 * LE CLIENT D'ABORD ET COCHÉ, les contacts ensuite et décochés : c'est
 * `clients.courriel` qui reçoit une confirmation dans le cas courant, et une
 * case déjà cochée pour chaque contact ferait partir le document à cinq
 * personnes au premier clic.
 *
 * LES DOUBLONS SONT ÉCARTÉS, casse comprise. Un contact dont l'adresse est
 * celle du client est fréquent — le gérant d'un petit chargeur est à la fois
 * la société et son contact. Deux cases pour la même boîte laisseraient croire
 * à deux destinataires.
 */
export function choixPossibles(proposes: DestinatairesProposes): readonly Choix[] {
  const choix: Choix[] = [];
  const vues = new Set<string>();

  const ajouter = (adresse: string | null, libelle: string, parDefaut: boolean) => {
    if (adresse === null) return;
    const propre = adresse.trim();
    if (propre === '') return;
    const clef = propre.toLowerCase();
    if (vues.has(clef)) return;
    vues.add(clef);
    choix.push({ adresse: propre, libelle, parDefaut });
  };

  ajouter(proposes.client.courriel, `${proposes.client.nom} (client)`, true);
  for (const contact of proposes.contacts) {
    const quoi = contact.fonction === null || contact.fonction.trim() === ''
      ? contact.nom
      : `${contact.nom} (${contact.fonction})`;
    ajouter(contact.courriel, quoi, false);
  }

  return choix;
}

/**
 * Peut-on envoyer ?
 *
 * LA PIÈCE JOINTE EST EXIGÉE ICI AUSSI, alors que la route la refuse déjà.
 * Ce n'est pas une redondance inutile : un bouton actif qui mène à un refus du
 * serveur est un bouton qui ment. Et la route garde la porte de son côté —
 * l'écran n'est pas ce qui protège.
 *
 * LE MOTIF DU REFUS EST RENDU, pas seulement `false` : l'écran l'affiche à
 * côté du bouton désactivé. Un bouton grisé sans explication est le défaut le
 * plus courant de ce genre de boîte.
 */
export type PourquoiPasEnvoyer =
  | { readonly peut: true }
  | { readonly peut: false; readonly pourquoi: string };

export function peutEnvoyer(etat: {
  readonly cochees: readonly string[];
  readonly pieceNom: string | null;
  readonly enCours: boolean;
  /**
   * `true` quand le client n'a AUCUNE adresse, ni lui ni ses contacts.
   *
   * AJOUTÉ APRÈS AVOIR REGARDÉ L'ÉCRAN, le 29/10. Sans ce cas, le motif
   * affiché à côté du bouton était « Cochez au moins un destinataire » — sous
   * un bandeau qui venait d'expliquer qu'il n'y a aucune adresse. On envoyait
   * l'utilisateur chercher des cases qui n'existent pas.
   *
   * Aucun test ne pouvait le voir : les deux messages sont justes
   * séparément, et c'est leur VOISINAGE qui ment.
   */
  readonly aucuneAdresse: boolean;
  /** `true` quand le champ de copie a été vidé. Voir plus bas. */
  readonly copieVide: boolean;
}): PourquoiPasEnvoyer {
  if (etat.enCours) {
    return { peut: false, pourquoi: 'Envoi en cours…' };
  }
  // `aucuneAdresse` N'EST PLUS UN BLOCAGE depuis le 29/10 : on peut taper une
  // adresse à la main. Le motif change seulement de mots, pour ne pas envoyer
  // chercher des cases qui n'existent pas.
  if (etat.cochees.length === 0) {
    return {
      peut: false,
      pourquoi: etat.aucuneAdresse
        ? 'Ce client n’a aucune adresse enregistrée : tapez-en une ci-dessus.'
        : 'Cochez au moins un destinataire.',
    };
  }
  // LA COPIE VIDE EST REFUSÉE ICI AUSSI, et la route la refuse de son côté.
  // Décision de Mohamed : « toujours au moins une ». Un bouton actif qui mène
  // à un refus du serveur est un bouton qui ment.
  if (etat.copieVide) {
    return {
      peut: false,
      pourquoi: 'L’adresse en copie ne peut pas être vide.',
    };
  }
  if (etat.pieceNom === null) {
    return {
      peut: false,
      pourquoi:
        'Joignez le PDF : le courriel renvoie au verso du document, il ne peut pas partir sans lui.',
    };
  }
  return { peut: true };
}

/**
 * LE NOM DE FICHIER SUGGÉRÉ, affiché pour guider l'impression.
 *
 * Ce n'est qu'une indication : le fichier joint garde le nom que le navigateur
 * lui a donné à l'enregistrement, et rien ici ne le renomme. Le suggérer évite
 * qu'un client reçoive une pièce nommée « document (3).pdf ».
 */
export function nomSuggere(numero: string): string {
  return `confirmation-${numero}.pdf`;
}

/**
 * CE QUI S'AFFICHE APRÈS L'ENVOI.
 *
 * Un succès nomme les destinataires ET la copie : la copie à l'exploitation
 * est visible, donc elle ne doit pas être une surprise. Un échec rend la
 * phrase du serveur telle quelle — « 550 adresse inconnue » dit quoi corriger,
 * « l'envoi a échoué » ne dit rien.
 */
export function messageApresEnvoi(resultat: {
  readonly parti: boolean;
  readonly message: string;
  readonly destinataires: readonly string[];
  /** Une LISTE depuis le 29/10 : la copie est choisie, et peut en compter plusieurs. */
  readonly copie: readonly string[];
}): { readonly ton: 'succes' | 'refus'; readonly texte: string } {
  if (resultat.parti) {
    return {
      ton: 'succes',
      texte:
        `Envoyé à ${resultat.destinataires.join(', ')}, ` +
        `avec ${resultat.copie.join(', ')} en copie.`,
    };
  }
  return {
    ton: 'refus',
    texte: `L’envoi a échoué. Le serveur répond : ${resultat.message}`,
  };
}

/**
 * LA FORME D'UNE ADRESSE, côté écran.
 *
 * MÊME EXPRESSION QUE LA ROUTE (`envoi_confirmation.ts`), et volontairement
 * lâche : les expressions « complètes » refusent des adresses parfaitement
 * valides — apostrophes, signes plus, domaines longs. Ce qu'on refuse ici,
 * c'est ce qui casserait le protocole : espace, virgule, point-virgule,
 * chevrons, retour à la ligne. Une adresse contenant un retour chariot
 * permettrait d'écrire des en-têtes de courriel supplémentaires.
 *
 * L'écran ne protège pas : il évite seulement de proposer un envoi que la
 * route refusera.
 */
const ADRESSE = /^[^\s,;<>"]+@[^\s,;<>"]+\.[^\s,;<>"]+$/;

export function adresseValide(texte: string): boolean {
  return ADRESSE.test(texte.trim());
}

/**
 * Devine un nom de contact à partir d'une adresse : `n.vasseur@lhtl.fr` donne
 * « N. Vasseur », `marie.dupont@x.fr` donne « Marie Dupont ».
 *
 * POURQUOI CETTE FONCTION EXISTE. La route d'ajout d'un contact EXIGE un nom
 * (`contacts.ts`, `texte(corps, 'nom', { obligatoire })`). Quand Stafa propose
 * de garder une adresse tapée à la main, il faut donc un nom — et le demander
 * en toutes lettres à chaque fois serait pénible. On en propose un, que
 * l'utilisateur corrige en deux secondes.
 *
 * ELLE DEVINE, ET C'EST ASSUMÉ. « compta@x.fr » donnera « Compta », qui n'est
 * le nom de personne. C'est un point de départ modifiable, pas une vérité : le
 * champ reste ouvert à la correction, et c'est précisément pour ça qu'on ne
 * l'ajoute pas automatiquement.
 */
export function nomDepuisAdresse(adresse: string): string {
  const gauche = adresse.trim().split('@')[0] ?? '';
  // `o'brien+factures` → `o'brien` : le suffixe après `+` est une étiquette de
  // tri, jamais un morceau de nom.
  const sansEtiquette = gauche.split('+')[0] ?? '';
  const morceaux = sansEtiquette
    .split(/[._]+/)
    .filter((m) => m !== '')
    .map((morceau) => {
      // Une seule lettre est une initiale : « n » → « N. ».
      if (morceau.length === 1) return `${morceau.toUpperCase()}.`;
      // La capitale revient aussi après un trait d'union : « jean-pierre ».
      return morceau
        .split('-')
        .map((part) =>
          part === '' ? part : part[0]!.toUpperCase() + part.slice(1).toLowerCase(),
        )
        .join('-');
    });
  const nom = morceaux.join(' ').trim();
  // Une adresse dont la partie gauche ne donne rien de lisible : on rend
  // l'adresse plutôt qu'une chaîne vide, que la route refuserait.
  return nom === '' ? adresse.trim() : nom;
}
